import type { AppIconSource } from '@shared-ui/assets/icons'
import appIconsMap from '@shared-ui/assets/icons/app-icons.json'
import { ActiveTouchAction, type ActiveTouchActionProps } from '@shared-ui/components/ui/active-touch-action'
import { localIcons } from '@shared-ui/components/ui/app-icon/icons'
import { createHitSlop } from '@shared-ui/platform/view'
import { useAppTheme } from '@shared-ui/theme'
import { MAX_SYSTEM_FONT_SCALE, MIN_SYSTEM_FONT_SCALE } from '@shared-ui/theme/scale'
import { type ElementType, memo, useMemo } from 'react'
import { StyleSheet, Text, type TextProps, type TextStyle, type ViewProps } from 'react-native'
import { useCreateStyles } from './styles'

export type AppIconSourcesExtended = AppIconSource | keyof typeof localIcons

// Плоские имена рендерят жирные/скруглённые варианты глифа (сохраняем прежнее поведение приложения).
const ICON_ALIASES: Partial<Record<string, AppIconSource>> = {
  close: 'close-bold',
  profile: 'profile-rounded',
  'profile-outline': 'profile-rounded-outline',
}

type AppIconProps = {
  icon: AppIconSourcesExtended
  size?: number
  color?: string
  style?: TextProps['style']
  isPrimary?: boolean
  isSuccess?: boolean
  isSecondary?: boolean
  hasCircle?: boolean
  isDanger?: boolean
  isWarning?: boolean
  testID?: TextProps['testID']
}

export { appIconsMap }

export const AppIcon = memo(function AppIconComponent(props: AppIconProps) {
  const { COLOR_ICON_BRAND, COLOR_ICON_SUCCESS, COLOR_ICON_CRITICAL, COLOR_ICON_SECONDARY, COLOR_ICON_WARNING } =
    useAppTheme()
  const styles = useCreateStyles()

  // собираем стиль для иконки
  const style = useMemo(() => {
    const flatten = (StyleSheet.flatten(props.style) ?? {}) as TextStyle
    const result: TextStyle[] = []

    if (props.color) result.push({ color: props.color })
    result.push({ fontSize: props.size || flatten.fontSize || 16 })

    if (props.isPrimary) result.push({ color: COLOR_ICON_BRAND })

    if (props.isSuccess) result.push({ color: COLOR_ICON_SUCCESS })

    if (props.isSecondary) result.push({ color: COLOR_ICON_SECONDARY })

    if (props.isDanger) result.push({ color: COLOR_ICON_CRITICAL })

    if (props.isWarning) result.push({ color: COLOR_ICON_WARNING })

    return StyleSheet.flatten(result)
  }, [
    props.style,
    props.color,
    props.size,
    props.isDanger,
    props.isPrimary,
    props.isSecondary,
    props.isSuccess,
    props.isWarning,
  ])

  // локальные SVG-иконки, которых нет в шрифте icomoon
  const isLocalIcon = (key: string): key is keyof typeof localIcons => {
    return key in localIcons
  }

  if (isLocalIcon(props.icon)) {
    const flatten = StyleSheet.flatten([styles.icon, style, props.style]) as { color?: string; fontSize?: number }
    const Component = localIcons[props.icon] as ElementType
    const color = props.color || flatten.color

    const size = props.size || (typeof flatten.fontSize === 'number' ? flatten.fontSize : undefined) || 16

    return <Component {...props} size={size} color={color} />
  }

  const iconName = ICON_ALIASES[props.icon] ?? props.icon

  if (!appIconsMap[iconName]) return null

  return (
    <Text
      selectable={false}
      style={[styles.icon, style, props.style]}
      testID={props.testID || props.icon}
      maxFontSizeMultiplier={MAX_SYSTEM_FONT_SCALE}
      minimumFontScale={MIN_SYSTEM_FONT_SCALE}
    >
      {String.fromCharCode(Number(appIconsMap[iconName]))}
    </Text>
  )
})

type CircleIconProps = {
  icon: AppIconSourcesExtended
  onPress: ActiveTouchActionProps['onPress']
  color?: string
  size?: number
  style?: ViewProps['style']
  styleIcon?: TextProps['style']
  testID?: ViewProps['testID']
}

export function CircleIcon(props: CircleIconProps) {
  const styles = useCreateStyles()
  const size = props.size || 24

  return (
    <ActiveTouchAction
      style={[styles.circle, props.style]}
      onPress={props.onPress}
      hitSlop={createHitSlop(15)}
      testID={props.testID || props.icon}
    >
      <AppIcon icon={props.icon} color={props.color} size={size} style={props.styleIcon} />
    </ActiveTouchAction>
  )
}
